import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { ApiError, lookupByName } from '../lib/api';
import { enabled as turnstileEnabled, load as loadTurnstile, SITE_KEY } from '../lib/turnstile';

/**
 * The box that matches a person to an invitation: their full name, and the
 * postcode we posted it to. It is the only way in — there is no bespoke link
 * and no printed code any more — so everything careful about it is now load
 * bearing rather than a second line of defence.
 *
 * It lives here rather than inside `/rsvp` because two places need it — the
 * RSVP page itself, and the dialog on the travel page that guards the room
 * block's booking link. One copy, because everything careful about it is easy
 * to undo by writing a second one that is friendlier.
 *
 * What it must not do, wherever it is used:
 *
 *   - validate the fields separately, or report on them separately. "That name
 *     is real but the postcode is wrong" is the one thing this form must never
 *     manage to say;
 *   - improve on the Worker's refusal. Every way of failing — a miss, a wrong
 *     postcode, a name shared by two households, a half-typed one — comes back
 *     as one sentence, and it is repeated here unembellished;
 *   - keep anything. A match sets a short-lived HttpOnly cookie at the
 *     Worker's end and hands this code nothing but a display name, so there is
 *     nothing here to hold on to or to leak.
 *
 * The reasoning in full is in `apps/api/src/party.ts`.
 */

type Props = {
  /** A match. The party pass is set by the time this fires. */
  onFound: () => void;
  /**
   * Whether the box is currently refusing. Only the boolean, because the
   * sentence is the Worker's and is already on screen — a caller uses this to
   * adjust its own surrounding copy, not to explain the failure a second time.
   */
  onFailureChange?: (failed: boolean) => void;
  /** What submitting means where this is used. */
  label?: string;
  busyLabel?: string;
};

export function FindForm({
  onFound,
  onFailureChange,
  label = 'Find my invitation',
  busyLabel = 'Looking…',
}: Props) {
  const [name, setName] = useState('');
  const [zip, setZip] = useState('');
  const [status, setStatus] = useState<'idle' | 'searching'>('idle');
  const [failure, setFailure] = useState('');
  /** Bots fill every field they find; nobody else can see this one. */
  const honeypot = useRef<HTMLInputElement>(null);

  /**
   * Turnstile's token for the next attempt, and the handle needed to ask for
   * another one. A token is single-use: spent on a submission whether or not
   * the lookup succeeded, so the widget is reset after every try. Without that
   * a guest who mistyped once would have their second, correct attempt
   * refused by a challenge rather than by the name.
   */
  const [token, setToken] = useState('');
  const widget = useRef<{ id: string; box: HTMLElement } | null>(null);
  const slot = useRef<HTMLDivElement>(null);

  /** Set in one place, so the surrounding copy and the message never disagree. */
  function showFailure(message: string) {
    setFailure(message);
    onFailureChange?.(message !== '');
  }

  /**
   * Enough to be worth sending: a name *and* a postcode. Checked only to keep
   * the button from submitting nothing — neither field is ever reported on
   * alone, and a wrong one gets the Worker's single sentence like everything
   * else.
   */
  const filledIn = name.trim().length >= 3 && zip.trim().length >= 5;
  const answerable = filledIn && (!turnstileEnabled || token !== '');

  /**
   * Mounts the widget with the form. Not in `index.html`: this is a
   * third-party script, and the two places that need it are a page nobody
   * lands on by accident and a dialog that opens on a click — so the home
   * page, which is nearly all the traffic, never fetches it.
   */
  useEffect(() => {
    if (!turnstileEnabled) return;
    let live = true;
    loadTurnstile()
      .then((turnstile) => {
        if (!live || !slot.current || widget.current) return;
        const id = turnstile.render(slot.current, {
          sitekey: SITE_KEY,
          theme: 'dark',
          callback: (value: string) => setToken(value),
          // A token that ran out, or a challenge that failed, leaves us with
          // nothing to send — which the button already knows how to reflect.
          'expired-callback': () => setToken(''),
          'error-callback': () => setToken(''),
        });
        if (id) widget.current = { id, box: slot.current };
      })
      .catch(() => {
        // The Worker will refuse the lookup, and say so in its own words. No
        // point in a second explanation here for something the guest cannot
        // act on until they try.
      });
    return () => {
      live = false;
      const held = widget.current;
      widget.current = null;
      if (held) window.turnstile?.remove(held.id);
    };
  }, []);

  async function onSubmit(event: JSX.TargetedEvent<HTMLFormElement, Event>) {
    event.preventDefault();
    if (status === 'searching') return;

    setStatus('searching');
    showFailure('');
    try {
      await lookupByName({
        name: name.trim(),
        zip: zip.trim(),
        turnstile: token,
        website: honeypot.current?.value ?? '',
      });
      onFound();
    } catch (error) {
      setStatus('idle');
      // Spent either way, so ask for another before the guest tries again.
      setToken('');
      if (widget.current) window.turnstile?.reset(widget.current.id);
      // The Worker's own wording, whatever it was. Do not embellish it: every
      // refusal is meant to read the same.
      showFailure(
        error instanceof ApiError
          ? error.message
          : 'Something went wrong. Please try again in a moment.',
      );
    }
  }

  return (
    <form class="find" onSubmit={onSubmit} noValidate>
      <div class={`field ${failure ? 'field--invalid' : ''}`}>
        <label class="field__label" for="lookup-name">
          Your full name
        </label>
        <input
          id="lookup-name"
          name="name"
          type="text"
          autocomplete="name"
          autocapitalize="words"
          enterkeyhint="next"
          value={name}
          maxLength={120}
          placeholder="As we would have written it"
          aria-invalid={failure ? 'true' : undefined}
          aria-describedby={failure ? 'lookup-error' : undefined}
          onInput={(e) => {
            setName(e.currentTarget.value);
            if (failure) showFailure('');
          }}
        />
      </div>

      <div class={`field ${failure ? 'field--invalid' : ''}`}>
        <label class="field__label" for="lookup-zip">
          And the postcode we sent it to
        </label>
        <input
          id="lookup-zip"
          name="zip"
          type="text"
          inputMode="numeric"
          autocomplete="postal-code"
          enterkeyhint="go"
          value={zip}
          maxLength={10}
          placeholder="5 digits"
          class="find__zip"
          aria-invalid={failure ? 'true' : undefined}
          aria-describedby={failure ? 'lookup-error' : undefined}
          onInput={(e) => {
            setZip(e.currentTarget.value);
            if (failure) showFailure('');
          }}
        />
        <p class="find__hint">
          First and last name, and the postcode from the envelope — we need both to be sure we have
          the right household.
        </p>
      </div>

      {/* Below the fields and above the button, which is where a guest looks
          last. Usually invisible — a managed challenge only shows itself when
          it has a reason to. */}
      {turnstileEnabled && <div class="find__challenge" ref={slot} />}

      {/* Honeypot — off-screen, unlabelled, ignored by password managers. */}
      <input
        ref={honeypot}
        type="text"
        name="website"
        tabIndex={-1}
        autocomplete="off"
        class="visually-hidden"
        aria-hidden="true"
      />

      {failure && (
        <p class="form__status form__status--error" id="lookup-error" role="alert">
          {failure}
        </p>
      )}

      <button
        class="btn btn--onForest"
        type="submit"
        disabled={status === 'searching' || !answerable}
      >
        {status === 'searching' ? busyLabel : label}
      </button>
    </form>
  );
}
